import { createContext } from 'react';

import type { PoiFeatureProperties } from '@/components/map/utils/poiFeatureProperties';
import type { VanFeatureProperties } from '@/components/map/utils/vanFeatureProperties';
import type { MapInteractionState, MapToolId } from './mapInteractionState';

/**
 * The command surface for the map-interaction slice. Commands mutate interaction
 * STATE only: no command touches the MapLibre instance (navigation stays with the
 * caller — useNetworkTableActions, useMapSearch, MapView handlers), and none writes
 * panel visibility (`setActivePanel` and panel-specific side effects such as
 * `filters.discardDraft()` stay composed in the owning MapView handler, e.g.
 * `filters.discardDraft(); commands.panelOpened(); setActivePanel('filters');`).
 */
export interface MapInteractionCommands {
    /**
     * Select a POI (opens details, derived). One command for every path — map click,
     * search select, Previous/Next navigation and table Show Details — selections
     * carry no source tag. Clears any located highlight.
     */
    selectPoi: (poi: PoiFeatureProperties, coords: [number, number] | null) => void;
    /** Table "Show Details" alias for {@link selectPoi} — kept for wiring readability. */
    showPoiDetails: (poi: PoiFeatureProperties, coords: [number, number] | null) => void;
    /**
     * Select a Find & Fix leak entity (opens the leak details panel, derived). Same
     * single-command discipline as {@link selectPoi}: every path — map click, search
     * select, table Show Details on a child row — converges here. Clears any located
     * highlight.
     */
    selectLeak: (leak: PoiFeatureProperties, coords: [number, number] | null) => void;
    /** Select a technician van (opens the van details panel, derived). Clears any located highlight. */
    selectVan: (van: VanFeatureProperties, coords: [number, number] | null) => void;
    /** Select an AOI (opens its stats card, derived). Clears any located highlight. */
    selectArea: (name: string, lngLat: [number, number]) => void;
    /** Select a DMA (opens its stats card, derived). Clears any located highlight. */
    selectDma: (ident: string, name: string, lngLat: [number, number]) => void;
    /** Highlight a POI without opening anything (table Locate). Closes any card/details. */
    locatePoi: (id: string) => void;
    /** Highlight an AOI without opening anything (table Locate). Closes any card/details. */
    locateArea: (name: string) => void;
    /** Highlight a DMA without opening anything (table Locate / DMA search). Closes any card/details. */
    locateDma: (ident: string, origin?: 'table' | 'search') => void;
    /** Close the open details/card: details close button, card close, Escape, semantic-zoom hide. */
    clearSelection: () => void;
    /** Empty-map click: one dismissal rule — clears the selection AND any located highlight. */
    emptyMapClick: () => void;
    /** A side panel opened: closes POI details only; an AOI/DMA card stays open beside panels. */
    panelOpened: () => void;
    /**
     * The table closed. 'explicit' (close button or map-control toggle) clears
     * table-originated highlights and any open POI details; 'reveal' (mobile Locate
     * auto-close) is a documented no-op — the located highlight is kept.
     */
    tableClosed: (reason: 'explicit' | 'reveal') => void;
    /** Search cleared: releases the search-originated highlight only; selection untouched. */
    searchCleared: () => void;
    /** Reset-to-network: back to a clean slate — clears everything. The fly-to stays with the caller. */
    resetToNetwork: () => void;
    /**
     * Activate a mutually exclusive map tool (radial measure). Closes any open
     * card/details/highlight; MapView suppresses feature interaction while a tool
     * is active. The tool's own geometry/cursor handling stays with its hook.
     */
    activateTool: (tool: MapToolId) => void;
    /** Deactivate the active tool (button toggle-off, Escape, tool cleanup). */
    deactivateTool: () => void;
}

/**
 * Split contexts so command-only consumers never re-render on state changes:
 * the commands object is referentially stable for the provider's lifetime.
 */
export const MapInteractionStateContext = createContext<MapInteractionState | null>(null);
export const MapInteractionCommandsContext = createContext<MapInteractionCommands | null>(null);
